/**
 * RuleBuilder — an automation rule written as the sentence it is.
 *
 *   WHEN     [an item changes to ▾] [deferred ▾]
 *   ONLY IF  it is in [Platform intake ▾] ✕     [add a condition ▾]
 *   THEN     [notify ▾] [the item's owner, or the admins if it has none ▾]
 *   AND      [tag it ▾] [stale] ✕
 *            [and also… ▾]
 *
 * Each blank is a picker; nothing is typed as an id or a placeholder. A rule
 * does up to five things (migration 832). Under the sentence: the rule read
 * back in prose, anything that would stop it from working with the one
 * control that fixes it, and a try-it check that runs the real engine on an
 * item and undoes it (preview_automation, migrations 826 and 832).
 *
 * Words and rules for the sentence live in src/lib/automationRules.ts; this
 * file only lays them out.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { X, Loader2 } from 'lucide-react';
import { Select, TextInput, Checkbox, Callout } from '@/components/ds';
import { Button } from '../../ui/Button';
import { btnPrimary, btnSecondary, heading, LABEL_MONO, panel, chip } from '../../mono/primitives';
import { SearchableItemPicker } from '../SearchableItemPicker';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { getDegapPhaseLabel } from '../../../lib/semanticDefaults';
import {
  AUTOMATION_WEBHOOK_EVENT,
  CONDITION_KEYS,
  CONDITION_LABEL,
  DEGAP_PHASES,
  EXIT_STATES,
  MAX_ACTIONS,
  RUN_AGENT_JOBS,
  SEVERITY_FLOORS,
  TRIGGERS,
  actionsFor,
  allActions,
  blankActionConfig,
  blankTriggerConfig,
  describeRule,
  missingPiece,
  notifyRecipientOptions,
  notifyTitle,
  previewSentence,
  ruleBlockers,
  subjectWord,
  takesConditions,
  toSaved,
  triggerMeta,
  withActions,
  type ActionKind,
  type AutomationDraft,
  type ConditionKey,
  type PreviewResult,
  type RuleAction,
  type RuleBlocker,
  type RuleLookups,
  type RuleReadiness,
  type TriggerKind,
} from '../../../lib/automationRules';
import type { SavedDraft } from '../../../lib/automationService';

type Option = { value: string; label: string };

export interface RuleBuilderOptions {
  users: Option[];
  queues: Array<Option & { hasDefaultOwner: boolean }>;
  groups: Option[];
  categories: Option[];
  agents: Option[];
  tags: string[];
}

export interface RuleBuilderProps {
  initial: AutomationDraft;
  isNew: boolean;
  lookups: RuleLookups;
  options: RuleBuilderOptions;
  readiness: RuleReadiness;
  onSwitchOnJob: (jobType: string) => Promise<void>;
  onSave: (saved: SavedDraft) => Promise<string | null>;
  onCancel: () => void;
  onPreview: (itemId: string, draft: AutomationDraft) => Promise<{ data: PreviewResult | null; error: string | null }>;
}

const ANY = '';
const INDENT = 'pl-[5.75rem]';

/** A picker sized to sit inside a line of text. */
function Pick({
  value,
  onChange,
  options,
  label,
  width = 'w-52',
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  label: string;
  width?: string;
  placeholder?: string;
}) {
  return (
    <span className={`inline-block ${width}`}>
      <Select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} options={options} />
    </span>
  );
}

function Words({ children }: { children: ReactNode }) {
  return <span className="text-[1rem] text-[var(--text-pri)]">{children}</span>;
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className={`${LABEL_MONO} w-20 shrink-0 pt-2.5`}>{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-2">{children}</div>
    </div>
  );
}

const ORDINAL = ['first', 'second', 'third', 'fourth', 'fifth'];

export function RuleBuilder({
  initial,
  isNew,
  lookups,
  options,
  readiness,
  onSwitchOnJob,
  onSave,
  onCancel,
  onPreview,
}: RuleBuilderProps) {
  const [draft, setDraft] = useState<AutomationDraft>(initial);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [tryItemId, setTryItemId] = useState<string | undefined>(undefined);
  const [preview, setPreview] = useState<{ text: string; error: boolean } | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const trigger = triggerMeta(draft.triggerKind);
  const actions = allActions(draft);
  const missing = missingPiece(draft);
  const sentence = describeRule(draft, lookups);
  const shownConditionKeys = takesConditions(draft.triggerKind)
    ? CONDITION_KEYS.filter((k) => k in (draft.conditions ?? {}))
    : [];
  const unusedConditions = CONDITION_KEYS.filter((k) => !shownConditionKeys.includes(k));
  const actionOptions = actionsFor(draft.triggerKind).map((a) => ({ value: a.kind, label: a.label }));

  // ── editing ───────────────────────────────────────────────────────────────
  const setTrigger = (kind: TriggerKind) =>
    setDraft((d) => {
      const allowed = actionsFor(kind).map((a) => a.kind);
      const owners = notifyRecipientOptions(kind).map((o) => o.value);
      const kept = allActions(d)
        .filter((a) => allowed.includes(a.kind))
        .map((a) => (a.kind === 'notify' && !owners.includes(String(a.config.recipients)) ? { kind: a.kind, config: blankActionConfig('notify') } : a));
      const next: AutomationDraft = {
        triggerKind: kind,
        triggerConfig: blankTriggerConfig(kind),
        conditions: takesConditions(kind) ? d.conditions : {},
        actionKind: d.actionKind,
        actionConfig: d.actionConfig,
      };
      return withActions(next, kept.length ? kept : [{ kind: 'notify', config: blankActionConfig('notify') }]);
    });
  const setTriggerIn = (key: string, value: unknown) =>
    setDraft((d) => {
      const next = { ...d.triggerConfig };
      if (value === ANY || value === undefined || value === null) delete next[key];
      else next[key] = value;
      return { ...d, triggerConfig: next };
    });
  const replaceActions = (fn: (list: RuleAction[]) => RuleAction[]) => setDraft((d) => withActions(d, fn(allActions(d))));
  const setActionKind = (i: number, kind: ActionKind) =>
    replaceActions((list) => list.map((a, j) => (j === i ? { kind, config: blankActionConfig(kind) } : a)));
  const setActionIn = (i: number, key: string, value: unknown) =>
    replaceActions((list) =>
      list.map((a, j) => {
        if (j !== i) return a;
        const config = { ...a.config };
        if (value === ANY || value === undefined || value === null) delete config[key];
        else config[key] = value;
        return { ...a, config };
      }),
    );
  const addAction = (kind: ActionKind) => replaceActions((list) => [...list, { kind, config: blankActionConfig(kind) }]);
  const removeAction = (i: number) => replaceActions((list) => list.filter((_, j) => j !== i));
  /** Keeps the clause even when its blank is emptied; only ✕ removes it. */
  const setCondition = (key: ConditionKey, value: unknown) =>
    setDraft((d) => ({ ...d, conditions: { ...d.conditions, [key]: value } }));
  const addCondition = (key: ConditionKey) =>
    setDraft((d) => ({ ...d, conditions: { ...d.conditions, [key]: key === 'steward_unassigned' ? true : key === 'tags_any' ? [] : '' } }));
  const removeCondition = (key: ConditionKey) =>
    setDraft((d) => {
      const next = { ...d.conditions };
      delete next[key];
      return { ...d, conditions: next };
    });

  // ── what would stop it from working ──────────────────────────────────────
  const switchOn = (jobType: string) => (
    <button
      type="button"
      {...btnSecondary()}
      disabled={switching !== null}
      onClick={() => {
        setSwitching(jobType);
        void onSwitchOnJob(jobType).finally(() => setSwitching(null));
      }}
    >
      {switching === jobType ? 'Switching on…' : 'Switch it on'}
    </button>
  );
  const blockerView = (b: RuleBlocker): { text: string; fix: ReactNode } => {
    switch (b.key) {
      case 'background_jobs_off':
        return {
          text: 'This rule runs on the background jobs, and they are off in this workspace, so it would never run.',
          fix: (
            <Link to="/prioritization/settings/modules" {...btnSecondary()}>
              Turn them on
            </Link>
          ),
        };
      case 'sweep_off':
        return {
          text: 'This rule runs on the background job that checks rules every 15 minutes, and that job is switched off.',
          fix: switchOn('automation_sweep'),
        };
      case 'job_off':
        return {
          text: `${RUN_AGENT_JOBS.find((j) => j.value === b.job)?.label ?? 'That job'} is switched off in this workspace, so running it would do nothing.`,
          fix: switchOn(b.job ?? ''),
        };
      case 'no_webhook_listener':
        return {
          text:
            readiness.webhookAddresses === 0
              ? 'No webhook address is registered in this workspace, so this would send nothing. A developer registers one with the Webhooks API.'
              : `None of the ${readiness.webhookAddresses} webhook addresses registered here listens for ${AUTOMATION_WEBHOOK_EVENT}, so this would send nothing. A developer adds that event to one with the Webhooks API.`,
          fix: (
            <Link to="/prioritization/documentation/api" {...btnSecondary()}>
              Webhooks API
            </Link>
          ),
        };
      case 'no_queue_default_owner':
        return {
          text: 'None of your queues has a default owner yet, so this would assign no one.',
          fix: (
            <Link to="/prioritization/settings/queues" {...btnSecondary()}>
              Set one in Queues
            </Link>
          ),
        };
    }
  };
  const blockers = ruleBlockers(draft, readiness).map((b) => ({ key: `${b.key}:${b.job ?? ''}`, ...blockerView(b) }));

  // ── try it ────────────────────────────────────────────────────────────────
  const items = useItemsStore((s) => s.items);
  useEffect(() => {
    if (items.length === 0) void useItemsStore.getState().fetchFromSupabase();
    // Load once; the store keeps itself current afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const canTry = trigger.subject !== 'cycle';
  const draftKey = JSON.stringify(draft);
  const lastRun = useRef(0);
  useEffect(() => {
    if (!tryItemId || !canTry || missing) {
      setPreview(null);
      return;
    }
    const run = ++lastRun.current;
    setPreviewing(true);
    const handle = window.setTimeout(() => {
      void onPreview(tryItemId, toSaved(draft, lookups)).then((res) => {
        if (run !== lastRun.current) return;
        setPreviewing(false);
        if (res.error || !res.data) setPreview({ text: res.error ?? 'The check could not run.', error: true });
        else setPreview({ text: previewSentence(res.data, draft, lookups), error: false });
      });
    }, 350);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryItemId, draftKey, canTry, missing]);

  const save = async () => {
    if (missing) return;
    setSaving(true);
    setSaveError(null);
    const err = await onSave(toSaved(draft, lookups));
    setSaving(false);
    if (err) setSaveError(err);
  };

  // ── the blanks ────────────────────────────────────────────────────────────
  const tc = draft.triggerConfig;
  const triggerBlanks = (() => {
    switch (draft.triggerKind) {
      case 'item_exit_status_changed':
        return (
          <Pick label="Which state" width="w-40" value={String(tc.to ?? ANY)} onChange={(v) => setTriggerIn('to', v)}
            options={[{ value: ANY, label: 'any state' }, ...EXIT_STATES.map((s) => ({ value: s, label: s }))]} />
        );
      case 'item_steward_assigned':
        return (
          <Pick label="Which person" value={String(tc.steward_id ?? ANY)} onChange={(v) => setTriggerIn('steward_id', v)}
            options={[{ value: ANY, label: 'anyone' }, ...options.users]} />
        );
      case 'item_queue_changed':
        return (
          <Pick label="Which queue" value={String(tc.to_queue_id ?? ANY)} onChange={(v) => setTriggerIn('to_queue_id', v)}
            options={[{ value: ANY, label: 'any queue' }, ...options.queues]} />
        );
      case 'item_tag_added':
        return (
          <span className="inline-block w-44">
            <TextInput aria-label="Which tag" list="automation-known-tags" placeholder="any tag" value={String(tc.tag ?? '')}
              onChange={(e) => setTriggerIn('tag', e.target.value)} />
          </span>
        );
      case 'item_idle_for_days':
        return (
          <>
            <span className="inline-block w-20">
              <TextInput aria-label="Days" type="number" min={1} step={1} value={String(tc.days ?? '')}
                onChange={(e) => setTriggerIn('days', e.target.value === '' ? ANY : Number(e.target.value))} />
            </span>
            <Words>days</Words>
          </>
        );
      case 'cycle_phase_changed':
        return (
          <Pick label="Which phase" width="w-44" value={String(tc.to_phase ?? ANY)} onChange={(v) => setTriggerIn('to_phase', v)}
            options={[{ value: ANY, label: 'any phase' }, ...DEGAP_PHASES.map((p) => ({ value: p, label: getDegapPhaseLabel(p) }))]} />
        );
      case 'agent_finding_raised':
        return (
          <>
            <Words>by</Words>
            <Pick label="Which agent" width="w-44" value={String(tc.agent_type ?? ANY)} onChange={(v) => setTriggerIn('agent_type', v)}
              options={[{ value: ANY, label: 'any agent' }, ...options.agents]} />
            <Words>rated</Words>
            <Pick label="How severe" width="w-48" value={String(tc.min_severity ?? ANY)} onChange={(v) => setTriggerIn('min_severity', v)}
              options={[{ value: ANY, label: 'any severity' }, ...SEVERITY_FLOORS.map((s) => ({ value: s.value, label: s.label }))]} />
          </>
        );
      case 'agent_finding_accepted':
        return (
          <>
            <Words>from</Words>
            <Pick label="Which agent" width="w-44" value={String(tc.agent_type ?? ANY)} onChange={(v) => setTriggerIn('agent_type', v)}
              options={[{ value: ANY, label: 'any agent' }, ...options.agents]} />
          </>
        );
      default:
        return null;
    }
  })();

  const conditionBlank = (key: ConditionKey) => {
    const c = draft.conditions;
    switch (key) {
      case 'queue_id':
        return (
          <Pick label="Queue" value={String(c.queue_id ?? '')} onChange={(v) => setCondition('queue_id', v)}
            placeholder="pick a queue" options={options.queues} />
        );
      case 'tags_any':
        return (
          <span className="inline-block w-44">
            <TextInput aria-label="Tag" list="automation-known-tags" placeholder="tag"
              value={Array.isArray(c.tags_any) ? String((c.tags_any as string[])[0] ?? '') : ''}
              onChange={(e) => setCondition('tags_any', e.target.value.trim() ? [e.target.value.trim()] : [])} />
          </span>
        );
      case 'item_category':
        return (
          <Pick label="Category" width="w-44" value={String(c.item_category ?? '')} onChange={(v) => setCondition('item_category', v)}
            placeholder="pick a category" options={options.categories} />
        );
      default:
        return null;
    }
  };

  /** The blanks after one action's picker. `n` names the action for screen readers. */
  const actionBlanks = (i: number, a: RuleAction) => {
    const ac = a.config;
    const n = actions.length > 1 ? ` (${ORDINAL[i]} action)` : '';
    switch (a.kind) {
      case 'notify': {
        const picked = Array.isArray(ac.user_ids) ? (ac.user_ids as string[]) : [];
        return (
          <>
            <Pick label={`Who to notify${n}`} width="w-[26rem] max-w-full" value={String(ac.recipients ?? 'steward')} onChange={(v) => setActionIn(i, 'recipients', v)}
              options={notifyRecipientOptions(draft.triggerKind)} />
            {ac.recipients === 'users' && (
              <>
                {picked.map((id) => (
                  <span key={id} {...chip('slate')}>
                    {lookups.userName?.(id) ?? 'someone'}
                    <Button variant="ghost" size="sm" aria-label={`Remove ${lookups.userName?.(id) ?? 'person'}`}
                      onClick={() => setActionIn(i, 'user_ids', picked.filter((x) => x !== id))}>
                      <X className="h-3 w-3" />
                    </Button>
                  </span>
                ))}
                <Pick label={`Add a person${n}`} width="w-48" value={ANY} placeholder={picked.length ? 'add another' : 'add a person'}
                  onChange={(v) => v && setActionIn(i, 'user_ids', [...picked, v])}
                  options={options.users.filter((u) => !picked.includes(u.value))} />
              </>
            )}
            {ac.recipients === 'group' && (
              <Pick label={`Which group${n}`} value={String(ac.group_id ?? '')} onChange={(v) => setActionIn(i, 'group_id', v)}
                placeholder="pick a group" options={options.groups} />
            )}
          </>
        );
      }
      case 'assign_steward':
        return (
          <>
            <Pick label={`Assign to${n}`} width="w-60" value={String(ac.steward ?? 'user')} onChange={(v) => setActionIn(i, 'steward', v)}
              options={[{ value: 'user', label: 'a person I pick' }, { value: 'queue_default', label: 'its queue’s default owner' }]} />
            {ac.steward !== 'queue_default' && (
              <Pick label={`Which person${n}`} value={String(ac.user_id ?? '')} onChange={(v) => setActionIn(i, 'user_id', v)}
                placeholder="pick a person" options={options.users} />
            )}
          </>
        );
      case 'move_queue':
        return (
          <Pick label={`Which queue${n}`} value={String(ac.queue_id ?? '')} onChange={(v) => setActionIn(i, 'queue_id', v)}
            placeholder="pick a queue" options={options.queues} />
        );
      case 'add_tag':
        return (
          <span className="inline-block w-44">
            <TextInput aria-label={`Tag to add${n}`} list="automation-known-tags" placeholder="tag" value={String(ac.tag ?? '')}
              onChange={(e) => setActionIn(i, 'tag', e.target.value)} />
          </span>
        );
      case 'set_exit_status':
        return (
          <Pick label={`New state${n}`} width="w-36" value={String(ac.to ?? 'deferred')} onChange={(v) => setActionIn(i, 'to', v)}
            options={EXIT_STATES.map((s) => ({ value: s, label: s }))} />
        );
      case 'run_agent':
        return (
          <>
            <Pick label={`What to run${n}`} width="w-60" value={String(ac.job_type ?? 'beacon_scan')} onChange={(v) => setActionIn(i, 'job_type', v)}
              options={RUN_AGENT_JOBS.map((j) => ({ value: j.value, label: j.label }))} />
            <Words>now</Words>
          </>
        );
      default:
        return null;
    }
  };

  /** Lines under an action: what a notification says, the owner checkbox, agent notes. */
  const actionNotes = (i: number, a: RuleAction) => {
    const ac = a.config;
    if (a.kind === 'notify') {
      return (
        <div className={`flex flex-wrap items-center gap-x-2 gap-y-2 ${INDENT} text-sm text-[var(--text-sec)]`}>
          <span>They see</span>
          {/* The title shown as a sample on its own ground, not inside quote
              marks: titles carry their own quotes (a tag rule's "was tagged
              “urgent”"), and wrapping them doubled the closing marks. */}
          <span className="bg-[var(--surface)] px-2 py-0.5 text-[var(--text-pri)]" data-testid="notify-sample">
            {titleParts.map((part, k) => (
              <span key={k}>
                {k > 0 && <em className="text-[var(--text-ter)]">{subjectWord(draft.triggerKind)}</em>}
                {part}
              </span>
            ))}
          </span>
          <span className="inline-block w-72">
            <TextInput aria-label={actions.length > 1 ? `Note (${ORDINAL[i]} action)` : 'Note'} size="sm" placeholder="add a note (optional)"
              value={String(ac.body ?? '')} onChange={(e) => setActionIn(i, 'body', e.target.value)} />
          </span>
        </div>
      );
    }
    if (a.kind === 'assign_steward') {
      return (
        <div className={INDENT}>
          <Checkbox label="Even if it already has an owner" checked={ac.overwrite === true}
            onChange={(e) => setActionIn(i, 'overwrite', e.target.checked ? true : ANY)} />
        </div>
      );
    }
    if (trigger.subject === 'finding' && a.kind === 'set_exit_status') {
      return (
        <p className={`${INDENT} text-sm text-[var(--text-sec)]`}>
          An agent starts this rule, so it never changes the item itself. It asks the workspace admins and the item&rsquo;s owner to.
        </p>
      );
    }
    if (trigger.subject === 'finding' && (a.kind === 'move_queue' || a.kind === 'add_tag')) {
      return <p className={`${INDENT} text-sm text-[var(--text-sec)]`}>It acts on the item the finding names, and skips findings that name none.</p>;
    }
    return null;
  };

  const titleParts = useMemo(() => notifyTitle(draft, lookups).split('{{subject}}'), [draft, lookups]);

  return (
    <section {...panel('p-5 space-y-5')} aria-label={isNew ? 'New rule' : 'Change this rule'}>
      <h3 {...heading()}>{isNew ? 'New rule' : 'Change this rule'}</h3>

      <datalist id="automation-known-tags">
        {options.tags.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>

      <div className="space-y-4">
        <Line label="When">
          <Pick label="What happens" width="w-72" value={draft.triggerKind} onChange={(v) => setTrigger(v as TriggerKind)}
            options={TRIGGERS.map((t) => ({ value: t.kind, label: t.label }))} />
          {triggerBlanks}
        </Line>

        {takesConditions(draft.triggerKind) && (
          <Line label="Only if">
            {shownConditionKeys.map((k, i) => (
              <span key={k} className="inline-flex flex-wrap items-center gap-2">
                {i > 0 && <Words>and</Words>}
                <Words>{CONDITION_LABEL[k]}</Words>
                {conditionBlank(k)}
                <Button variant="ghost" size="sm" aria-label={`Remove “${CONDITION_LABEL[k]}”`} onClick={() => removeCondition(k)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </span>
            ))}
            {unusedConditions.length > 0 && (
              <Pick label="Add a condition" width="w-56" value={ANY}
                placeholder={shownConditionKeys.length ? 'and also…' : 'no conditions (add one)'}
                onChange={(v) => v && addCondition(v as ConditionKey)}
                options={unusedConditions.map((k) => ({ value: k, label: CONDITION_LABEL[k] }))} />
            )}
          </Line>
        )}

        {actions.map((a, i) => (
          <div key={i} className="space-y-3" data-testid="rule-action">
            <Line label={i === 0 ? 'Then' : 'And'}>
              <Pick label={actions.length > 1 ? `What to do (${ORDINAL[i]} action)` : 'What to do'} width="w-60" value={a.kind}
                onChange={(v) => setActionKind(i, v as ActionKind)} options={actionOptions} />
              {actionBlanks(i, a)}
              {i > 0 && (
                <Button variant="ghost" size="sm" aria-label={`Remove the ${ORDINAL[i]} action`} onClick={() => removeAction(i)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </Line>
            {actionNotes(i, a)}
          </div>
        ))}

        {actions.length < MAX_ACTIONS && (
          <Line label="">
            <Pick label="Add another action" width="w-60" value={ANY} placeholder="and also… (add an action)"
              onChange={(v) => v && addAction(v as ActionKind)} options={actionOptions} />
          </Line>
        )}
      </div>

      {blockers.map((b) => (
        <Callout key={b.key} tone="coral" action={b.fix}>
          {b.text}
        </Callout>
      ))}

      <div className="border-t pt-4 space-y-4" style={{ borderColor: 'var(--rule)' }}>
        <p className="font-serif text-lg leading-snug text-[var(--text-pri)]" data-testid="rule-sentence">
          {sentence}
        </p>

        {canTry && (
          <div className="max-w-xl space-y-2">
            <SearchableItemPicker
              label="Try it on an item"
              helperText="Runs the rule on the item you pick, shows what it would do, and undoes it. Nothing is changed or sent."
              value={tryItemId}
              onChange={setTryItemId}
              placeholder="Search items…"
              allowEmpty
              emptyLabel="No item"
            />
            {tryItemId && missing && <p className="text-sm text-[var(--text-ter)]">Finish the rule to try it.</p>}
            {tryItemId && !missing && (
              <p className={`text-sm ${preview?.error ? 'text-[var(--coral)]' : 'text-[var(--text-pri)]'}`} data-testid="rule-preview" aria-live="polite">
                {previewing ? (
                  <span className="inline-flex items-center gap-2 text-[var(--text-ter)]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Trying it…
                  </span>
                ) : (
                  preview?.text
                )}
              </p>
            )}
          </div>
        )}

        {missing && <p className="text-sm text-[var(--text-sec)]">{missing}</p>}
        {saveError && <p className="text-sm text-[var(--coral)]" role="alert">{saveError}</p>}

        <div className="flex items-center gap-2">
          <button type="button" {...btnPrimary()} disabled={!!missing || saving} onClick={() => void save()}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {isNew ? 'Save rule' : 'Save changes'}
          </button>
          <button type="button" {...btnSecondary()} disabled={saving} onClick={onCancel}>
            Cancel
          </button>
          <Link to="/prioritization/documentation/automations" className="ml-auto text-sm text-[var(--text-ter)] underline hover:text-[var(--text-pri)]">
            How rules work
          </Link>
        </div>
      </div>
    </section>
  );
}
